import React, { useEffect, useState } from "react";
import { FileBarChart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inline = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
  p.startsWith("**") ? <strong key={i} className="text-foreground">{p.slice(2, -2)}</strong> : p);

const cells = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

function Markdown({ text }) {
  const lines = text.split("\n");
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (l.startsWith("```")) {
      const code = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) { code.push(lines[i]); i++; }
      out.push(<pre key={i} dir="ltr" className="text-xs bg-secondary rounded-lg p-3 my-2 whitespace-pre-wrap text-start select-all">{code.join("\n")}</pre>);
    } else if (l.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) { rows.push(lines[i]); i++; }
      i--;
      const [head, , ...body] = rows;
      out.push(
        <div key={i} className="overflow-x-auto my-3">
          <table className="w-full text-xs border border-border rounded-lg">
            <thead><tr>{cells(head).map((c, k) => <th key={k} className="bg-secondary p-2 text-start font-bold">{inline(c)}</th>)}</tr></thead>
            <tbody>{body.map((r, j) => <tr key={j} className="border-t border-border">{cells(r).map((c, k) => <td key={k} className="p-2 align-top">{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>);
    } else if (l.startsWith("# ")) out.push(<h1 key={i} className="text-2xl font-black mb-1">{l.slice(2)}</h1>);
    else if (l.startsWith("## ")) out.push(<h2 key={i} className="text-lg font-bold text-primary mt-6 mb-2">{l.slice(3)}</h2>);
    else if (l.startsWith("### ")) out.push(<h3 key={i} className="font-bold mt-4 mb-1">{l.slice(4)}</h3>);
    else if (l.startsWith("> ")) out.push(<p key={i} className="text-xs text-muted-foreground border-r-2 border-accent pr-3 my-2">{inline(l.slice(2))}</p>);
    else if (l.startsWith("- [ ] ")) out.push(<p key={i} className="text-sm my-1 pr-2">☐ {inline(l.slice(6))}</p>);
    else if (/^(\d+\.|-) /.test(l)) out.push(<p key={i} className="text-sm my-1 pr-2">• {inline(l.replace(/^(\d+\.|-) /, ""))}</p>);
    else if (l.trim() === "---") out.push(<hr key={i} className="border-border my-4" />);
    else if (l.trim()) out.push(<p key={i} className="text-sm my-1 text-muted-foreground">{inline(l)}</p>);
  }
  return <div dir="rtl">{out}</div>;
}

export const AdminStudy = () => {
  const [docs, setDocs] = useState([]);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/docs").then((r) => setDocs(r.data)).catch(() => {}); }, []);
  const open = async (id) => {
    setBusy(id);
    try { const { data } = await api.get(`/admin/docs/${id}`); setDoc(data); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return (
    <div className="glass rounded-2xl p-5 border border-border" data-testid="admin-docs-section">
      <div className="font-bold flex items-center gap-2 mb-1"><FileBarChart className="w-4 h-4 text-primary" /> وثائق الأدمن — الدراسة وأدلة التفعيل والاعتمادات</div>
      <p className="text-xs text-muted-foreground mb-3">الدراسة تُحدَّث تلقائيًا مع كل تحديث للمنصة؛ أدلة التفعيل خطوة بخطوة لاعتمادات Meta (سوبر رواج والسكرتير التنفيذي).</p>
      <div className="grid md:grid-cols-3 gap-3">
        {docs.map((d) => (
          <div key={d.id} className="rounded-xl border border-border bg-secondary/40 p-3 flex flex-col gap-2" data-testid={`admin-doc-${d.id}`}>
            <div className="text-sm font-bold">{d.title} {d.auto && <span className="text-[10px] font-normal rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-2 py-px">تلقائي</span>}</div>
            <p className="text-[11px] text-muted-foreground flex-1">{d.desc}</p>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] text-muted-foreground">آخر تحديث: {new Date(d.updated_at).toLocaleDateString("ar-SA")}</span>
              <Button size="sm" onClick={() => open(d.id)} disabled={busy === d.id} className="gap-1" data-testid={`admin-doc-open-${d.id}`}>
                {busy === d.id && <Loader2 className="w-3 h-3 animate-spin" />} عرض
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!doc} onOpenChange={(o) => !o && setDoc(null)}>
        <DialogContent className="bg-popover border-border max-w-4xl max-h-[88vh] overflow-y-auto" dir="rtl" data-testid="admin-study-dialog">
          <DialogHeader><DialogTitle className="sr-only">{doc?.title}</DialogTitle></DialogHeader>
          {doc && <Markdown text={doc.markdown} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
